import React, { useState } from 'react';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';
import { useHistory } from 'react-router-dom';

// Contexts
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { useConfiguration } from '../../../../context/configurationContext';

// Utility
import { FormattedMessage } from '../../../../util/reactIntl';
import { createResourceLocatorString } from '../../../../util/routes';
import { isOriginInUse } from '../../../../util/search';
import { stringifyDateToISO8601 } from '../../../../util/dates';
import { formatMultiEnumValue } from '../../../../util/caregiverSearch';
import {
  hasManualLocationFallbackSearch,
  isValidManualLocationFallbackSearch,
  normalizeManualLocationFallbackSearch,
} from '../../../../util/locationSearchFallback';

// Shared components
import { Form, PrimaryButton } from '../../../../components';

import FilterDateRange from './FilterDateRange/FilterDateRange';
import FilterLocation from './FilterLocation/FilterLocation';
import FilterKeyword from './FilterKeyword/FilterKeyword';

import css from './SearchCTA.module.css';

const GRID_CONFIG = [
  { gridCss: css.gridCol1 },
  { gridCss: css.gridCol2 },
  { gridCss: css.gridCol3 },
  { gridCss: css.gridCol4 },
];

const getGridCount = numberOfFields => {
  const gridConfig = GRID_CONFIG[numberOfFields - 1];
  return gridConfig ? gridConfig.gridCss : GRID_CONFIG[0].gridCss;
};

const isEmpty = value => {
  if (value == null) return true;
  return value.hasOwnProperty('length') && value.length === 0;
};

const formatDateValue = (dateRange, queryParamName) => {
  const hasDates = dateRange;
  const { startDate, endDate } = hasDates ? dateRange : {};
  const start = startDate ? stringifyDateToISO8601(startDate) : null;
  const end = endDate ? stringifyDateToISO8601(endDate) : null;
  const value = start && end ? `${start},${end}` : null;
  return { [queryParamName]: value };
};

export const SearchCTA = React.forwardRef((props, ref) => {
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();

  const { categories, dateRange, keywordSearch, locationSearch } = props.searchFields;

  const [submitDisabled, setSubmitDisabled] = useState(false);

  const filters = {
    categories: {
      enabled: categories,
      isValid: () => categories,
    },
    keywordSearch: {
      enabled: keywordSearch || categories,
      isValid: () => keywordSearch || categories,
      render: () => (
        <div className={css.filterField} key="keywordSearch">
          <FilterKeyword />
        </div>
      ),
    },
    locationSearch: {
      enabled: locationSearch,
      isValid: () => locationSearch,
      render: alignLeft => (
        <div className={css.filterField} key="locationSearch">
          <FilterLocation setSubmitDisabled={setSubmitDisabled} alignLeft={alignLeft} />
        </div>
      ),
    },

    dateRange: {
      enabled: dateRange,
      isValid: () => dateRange,
      render: alignLeft => (
        <div className={css.filterField} key="dateRange">
          <FilterDateRange config={config} alignLeft={alignLeft} />
        </div>
      ),
    },
  };

  const filterOrder = ['keywordSearch', 'locationSearch', 'dateRange'];
  const fieldCount = filterOrder.filter(key => filters[key]?.enabled && filters[key]?.isValid())
    .length;

  if (fieldCount === 0) {
    return null;
  }

  const onSubmit = values => {
    // Convert form values to query parameters
    let queryParams = {};

    Object.entries(values).forEach(([key, value]) => {
      if (!isEmpty(value)) {
        if (key == 'dateRange') {
          const { dates } = formatDateValue(value, 'dates');
          queryParams.dates = dates;
        } else if (key === 'pub_category') {
          queryParams.pub_category = value;
          queryParams.pub_serviceCategories = formatMultiEnumValue([value]);
        } else if (key == 'location') {
          if (value.selectedPlace) {
            const {
              search,
              selectedPlace: { origin, bounds },
            } = value;
            queryParams.bounds = bounds;
            queryParams.address = search;

            if (isOriginInUse(config) && origin) {
              queryParams.origin = `${origin.lat},${origin.lng}`;
            }
          } else if (isValidManualLocationFallbackSearch(value.search)) {
            queryParams.address = normalizeManualLocationFallbackSearch(value.search);
          }
        } else {
          queryParams[key] = value;
        }
      }
    });

    const to = createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams);
    // Use history.push to navigate without page refresh
    history.push(to);
  };

  return (
    <div className={css.searchBarContainer}>
      <FinalForm
        onSubmit={onSubmit}
        {...props}
        render={({ handleSubmit, invalid, values }) => {
          const hasInvalidLocationFallback =
            hasManualLocationFallbackSearch(values?.location?.search) &&
            !values?.location?.selectedPlace &&
            !isValidManualLocationFallbackSearch(values?.location?.search);
          return (
            <Form role="search" onSubmit={handleSubmit} className={css.form}>
              <div className={classNames(css.gridContainer, getGridCount(fieldCount || 1))}>
                {filterOrder.map(key => {
                  const filter = filters[key];

                  if (!filter?.enabled || !filter?.isValid()) {
                    return null;
                  }

                  if (key === 'keywordSearch') {
                    return (
                      <div className={css.filterField} key="keywordSearch">
                        <FilterKeyword />
                      </div>
                    );
                  }

                  return filter.render(true);
                })}

                <span className={css.submitButtonWrapper}>
                  <PrimaryButton
                    disabled={submitDisabled || invalid}
                    className={css.submitButton}
                    type="submit"
                  >
                    <FormattedMessage id="PageBuilder.SearchCTA.buttonLabel" />
                  </PrimaryButton>
                </span>
                {hasInvalidLocationFallback ? (
                  <p className={css.submitHelper}>
                    <FormattedMessage id="SearchPage.mapApplyDisabledTooltip" />
                  </p>
                ) : null}
              </div>
            </Form>
          );
        }}
      />
    </div>
  );
});

SearchCTA.displayName = 'SearchCTA';
